import React from 'react';
import type { PlantSummary, PlantAvgTime } from '../../types/adminDashboard';

interface Props {
  summary: PlantSummary[] | null;
  avgTime: PlantAvgTime[] | null;
}

export default function PlantSummaryCards({ summary, avgTime }: Props) {
  if (!summary || summary.length === 0) {
    return (
      <section style={sectionStyle}>
        <Header />
        <div style={emptyStyle}>No plant data available</div>
      </section>
    );
  }

  const timeMap = new Map(
    (avgTime || []).map((t) => [t.plant, t]),
  );
  const rows = [...summary].sort((a, b) => b.total - a.total || a.plant.localeCompare(b.plant));

  return (
    <section style={sectionStyle}>
      <Header />
      <div style={tableWrapStyle}>
        <div style={gridHeaderStyle}>
          <span>Plant</span>
          <span>Status mix</span>
          <span>Total</span>
          <span>Done</span>
          <span>Active</span>
          <span>Todo</span>
          <span>Avg time</span>
        </div>
        {rows.map((plant) => {
          const time = timeMap.get(plant.plant);
          const completion = plant.total > 0 ? Math.round((plant.completed / plant.total) * 100) : 0;
          const doneWidth = percentWidth(plant.completed, plant.total);
          const activeWidth = percentWidth(plant.in_progress, plant.total);
          const todoWidth = percentWidth(plant.todo, plant.total);
          const avgTimeLabel = time && time.diagrams_with_time > 0
            ? `${time.avg_minutes}m`
            : '-';

          return (
            <div
              key={plant.plant}
              style={gridRowStyle}
            >
              <div style={plantCellStyle}>
                <strong style={plantNameStyle}>{plant.plant}</strong>
                <span style={plantMetaStyle}>{completion}% complete</span>
              </div>
              <div style={progressCellStyle}>
                <div style={progressTrackStyle} aria-label={`${plant.plant} progress`}>
                  <span style={{ ...progressSegmentStyle, width: doneWidth, backgroundColor: '#059669' }} />
                  <span style={{ ...progressSegmentStyle, width: activeWidth, backgroundColor: '#d97706' }} />
                  <span style={{ ...progressSegmentStyle, width: todoWidth, backgroundColor: '#cbd5e1' }} />
                </div>
              </div>
              <span style={numberCellStyle}>{plant.total}</span>
              <span style={numberCellStyle}>{plant.completed}</span>
              <span style={numberCellStyle}>{plant.in_progress}</span>
              <span style={numberCellStyle}>{plant.todo}</span>
              <span style={numberCellStyle}>{avgTimeLabel}</span>
            </div>
          );
        })}
      </div>
    </section>
  );
}

function Header() {
  return (
    <div style={headerStyle}>
      <div>
        <h2 style={titleStyle}>Plant progress</h2>
        <p style={subtitleStyle}>Completed, active, and waiting diagrams by plant.</p>
      </div>
      <div style={legendStyle}>
        <LegendDot color="#059669" label="Done" />
        <LegendDot color="#d97706" label="Active" />
        <LegendDot color="#cbd5e1" label="Todo" />
      </div>
    </div>
  );
}

function LegendDot({ color, label }: { color: string; label: string }) {
  return (
    <span style={legendItemStyle}>
      <span style={{ ...dotStyle, backgroundColor: color }} />
      {label}
    </span>
  );
}

function percentWidth(value: number, total: number): string {
  if (total <= 0 || value <= 0) return '0%';
  return `${Math.max((value / total) * 100, 2)}%`;
}

const sectionStyle: React.CSSProperties = {
  backgroundColor: '#fff',
  border: '1px solid #e2e8f0',
  borderRadius: 8,
  overflow: 'hidden',
};

const headerStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'flex-start',
  justifyContent: 'space-between',
  gap: 16,
  padding: '18px 20px',
  borderBottom: '1px solid #e2e8f0',
  flexWrap: 'wrap',
};

const titleStyle: React.CSSProperties = {
  margin: 0,
  fontSize: 17,
  lineHeight: 1.25,
  fontWeight: 750,
  color: '#0f172a',
};

const subtitleStyle: React.CSSProperties = {
  margin: '4px 0 0',
  fontSize: 13,
  color: '#64748b',
};

const legendStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 12,
  flexWrap: 'wrap',
};

const legendItemStyle: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 6,
  fontSize: 12,
  fontWeight: 650,
  color: '#475569',
};

const dotStyle: React.CSSProperties = {
  width: 8,
  height: 8,
  borderRadius: 999,
  display: 'inline-block',
};

const tableWrapStyle: React.CSSProperties = {
  overflowX: 'auto',
};

const gridHeaderStyle: React.CSSProperties = {
  minWidth: 820,
  display: 'grid',
  gridTemplateColumns: 'minmax(160px, 1.2fr) minmax(220px, 2fr) repeat(5, minmax(72px, 0.55fr))',
  gap: 12,
  padding: '10px 20px',
  fontSize: 11,
  fontWeight: 750,
  color: '#64748b',
  textTransform: 'uppercase',
  letterSpacing: '0.04em',
  backgroundColor: '#f8fafc',
  borderBottom: '1px solid #e2e8f0',
};

const gridRowStyle: React.CSSProperties = {
  minWidth: 820,
  display: 'grid',
  gridTemplateColumns: 'minmax(160px, 1.2fr) minmax(220px, 2fr) repeat(5, minmax(72px, 0.55fr))',
  alignItems: 'center',
  gap: 12,
  padding: '14px 20px',
  borderBottom: '1px solid #f1f5f9',
};

const plantCellStyle: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  minWidth: 0,
};

const plantNameStyle: React.CSSProperties = {
  fontSize: 14,
  color: '#0f172a',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
};

const plantMetaStyle: React.CSSProperties = {
  fontSize: 12,
  color: '#64748b',
  marginTop: 2,
};

const progressCellStyle: React.CSSProperties = {
  minWidth: 0,
};

const progressTrackStyle: React.CSSProperties = {
  width: '100%',
  height: 10,
  display: 'flex',
  overflow: 'hidden',
  borderRadius: 999,
  backgroundColor: '#e2e8f0',
};

const progressSegmentStyle: React.CSSProperties = {
  height: '100%',
  display: 'block',
};

const numberCellStyle: React.CSSProperties = {
  fontSize: 13,
  color: '#334155',
  fontWeight: 650,
};

const emptyStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  minHeight: 120,
  color: '#9ca3af',
  fontSize: 14,
};
